import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import wtprCountriesJson from '../../../../../wtpr/wtpr_countries.json';
import { GlobalGuideService } from '../../global-guide.service';

const YSCALE = 0.82;

@Component({
  selector: 'global-guide-world-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit {
  countriesJson = JSON.parse(JSON.stringify(wtprCountriesJson));
  country = '';
  initialized;
  isHovering = false;
  geo;
  @ViewChild('map') mapRef: ElementRef;
  metricValue = '';
  mouseX;
  mouseY;
  path;
  @ViewChild('popover') popover: ElementRef;
  projection;
  svg;
  transform;
  zoom;

  constructor(private service: GlobalGuideService) {
  }

  ngOnInit() {}

  ngOnChanges() {
    if (this.initialized) {
      this.update();
    }
  }

  ngAfterViewInit() {
    if (!this.initialized) {
      this.initialize();
    }
  }

  /**
   * Captures the mouse.
   * @todo There are numerical constants here!  Shouldn't be!
   */
  captureMouse(event) {
    this.mouseX = event.offsetX;
    this.mouseY = event.offsetY;
    const height = this.popover.nativeElement.clientHeight;
    const width = this.popover.nativeElement.clientWidth;
    // TODO: Numerical constant.
    this.popover.nativeElement.style.left = `${this.mouseX - width / 2}px`;
    this.popover.nativeElement.style.top = `${this.mouseY - height / 2 - 48}px`;
  }

  /**
   * Handles a click for the map.
   */
  handleClick(pathData) {
    const { id } = pathData;
    this.service.selectedCountry = this.service.countriesByIsoCode[id].name;
  }

  /**
   * Hides the tooltip.
   */
  hideTooltip() {
    this.isHovering = false;
    this.country = '';
    this.metricValue = '';
  }

  /**
   * Initialize the SVG for the map.
   * @todo There are numerical constants here!  Shouldn't be!
   */
  initialize() {
    this.path = d3.geoPath();
    // TODO: Width / Height as constants please!
    this.projection = d3.geoMercator().fitSize([546, 440], this.countriesJson);
    this.geo = this.path.projection(this.projection);
    this.svg = d3.select(this.mapRef.nativeElement)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 546 440`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    this.svg.append('g')
      .attr('class', 'map');

    // Remove Antarctica as the spec doesn't want include it.
    this.countriesJson.features = this.countriesJson.features
      .filter(({ properties }) => {
        return properties.name !== 'Antarctica';
      });
    
    const showTooltip = this.showTooltip.bind(this);
    const hideTooltip = this.hideTooltip.bind(this);
    const handleClick = this.handleClick.bind(this);
    const update = this.update.bind(this);
    this.svg.select('.map')
      .selectAll('path')
      .data(this.countriesJson.features)
      .enter()
      .append('svg:a')
      .append('path')
      .attr('d', this.path)
      .on('mouseover', function (pathData) {
        // eslint-disable-next-line
        const node = this;
        showTooltip(pathData, node);
      })
      .on('click', function (pathData) {
        // eslint-disable-next-line
        const node = this;
        handleClick(pathData, node);
      })
      .on('mouseout', function () {
        hideTooltip();
        update();
      })
      // TODO: Color constant.
      // Grey color
      .style('stroke', '#E2E4E6')
      .style('stroke-width', 1)
      .style('fill', '#fff');
    this.initializeZoom();
    this.update();
    this.initialized = true;
  }

  /**
   * Initializes the zoom feature.
   * @todo There are numerical constants here!  Shouldn't be!
   */
  initializeZoom() {
    const svg = this.svg;
    // TODO: Numerical constant.
    const zoom = this.zoom = d3.zoom()
      .extent([[0 - 546 / 2, 0], [546 / 2, 300 / 2]])
      .scaleExtent([0.2, 2])
      .on('zoom', () => {
        const { transform } = d3.event;
        this.transform = transform;
        svg.select('.map')
          .attr('transform', `translate(${transform.x}, ${transform.y})scale(${transform.k}, ${transform.k * YSCALE})`);
      });
    this.svg
      .call(zoom)
      .call(zoom.transform, d3.zoomIdentity.translate(-110, 20).scale(1.32));
  }

  /**
   * Shows the tooltip with the hovered country name as well as the selected
   * metric value.
   */
  showTooltip(pathData, node) {
    this.update();
    const { id } = pathData;
    if (this.service.countriesByIsoCode[id]) {
      this.isHovering = true;
      this.country = this.service.countriesByIsoCode[id].name;
      this.metricValue = this.service.countriesByIsoCode[id].metrics[this.service.activeMetric];
    }
    d3.select(node)
    // TODO: Color constant.
      .style('stroke', () => {
        if (this.service.countriesByIsoCode[id]) {
          return '#A39EDD';
        } else {
          return node.style.stroke;
        }
      })
      // TODO: Color constant.
      .style('fill', () => {
        if (this.service.countriesByIsoCode[id]) {
          return '#C1BEE8';
        } else {
          return node.style.fill;
        }
      });
  }

  /**
   * Updates the map with the appropriate colorization.
   */
  update() {
    this.svg.select('.map')
      .selectAll('path')
      // TODO: Color constant.
      .style('stroke', (country) => {
        const { id } = country;
        if (this.service.countriesByIsoCode[id]) {
          return '#C1BEE8';
        }
        return '#E2E4E6';
      })
      // TODO: Color constant.
      .style('fill', (country) => {
        const { id } = country;
        if (this.service.countriesByIsoCode[id]) {
          return '#E0DFF4';
        }
        return '#fff';
      });
  }

  /**
   * Zooms in.
   * @todo Refactor this into one FN.
   */
  zoomIn() {
    const delta = (this.zoom.scaleExtent()[1] - this.zoom.scaleExtent()[0]) /
      100;
    this.transform.k += delta;
    const transform = this.transform;
    this.svg.select('.map')
      .attr('transform', `translate(${transform.x}, ${transform.y})` +
        `scale(${transform.k}, ${transform.k * YSCALE})`);
  }

  /**
   * Zooms out.
   * @todo Refactor this into one FN.
   */
  zoomOut() {
    const delta = (this.zoom.scaleExtent()[1] - this.zoom.scaleExtent()[0]) /
      100;
    this.transform.k -= delta;
    const transform = this.transform;
    this.svg.select('.map')
      .attr('transform', `translate(${transform.x}, ${transform.y})` +
        `scale(${transform.k}, ${transform.k * YSCALE})`);
  }
}
